import { animate, motion, useReducedMotion } from "framer-motion";
import { Check, Info, Instagram } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import cn from "~/utils/cn";
import { formatMoney, formatNumber, formatPercent } from "../../plans";
import Button from "../../ui/Button";
import Reveal from "../../ui/Reveal";
import SectionTitle from "../shared/SectionTitle";
import { EquivalentArt, EquivalentLine } from "../../equivalents/PriceEquivalent";
import { planEquivalents } from "../../equivalents/pick";
import { checkoutHref, INSTAGRAM_URL, type LandingData } from "../types";

const FEATURES = ["fonts", "backgrounds", "import", "ads"] as const;

// Plan picker: one segmented duration control drives a single large price read-out and the buy button.
export default function PlansB({ data }: { data: LandingData }) {
  const { t } = useTranslation();
  const { plans, currency } = data;
  const uid = useId();
  const titleId = `${uid}-plans`;
  const bestId = `${uid}-best`;
  const reduce = useReducedMotion();
  const best = plans.findIndex((p) => p.isBest);
  const [sel, setSel] = useState(0); // the cheapest (monthly) plan
  const radios = useRef<(HTMLButtonElement | null)[]>([]);
  const plan = plans[sel];
  // everyday item at the same price (only when the Toman price is known)
  const eq = plan ? planEquivalents(plans, currency?.code)[plan.id] : null;

  const section = "mx-auto w-full max-w-[1200px] scroll-mt-[var(--nav-h)] px-[var(--gutter)] pb-[clamp(64px,9vw,128px)]";
  const head = (
    <SectionTitle id={titleId} eyebrow={t("ui.landing.hero.eyebrow")} title={t("ui.landing.plans.title")} lead={t("ui.landing.plans.subtitle")} />
  );

  if (!plan)
    return (
      <section id="plans" aria-labelledby={titleId} className={section}>
        {head}
        <div className="mt-8 flex max-w-[42rem] flex-col items-start gap-5 rounded-lg border border-line bg-surface p-[clamp(24px,3vw,40px)] shadow-card">
          <p className="leading-[1.9] text-ink-2">{t("ui.landing.plans.unavailable")}</p>
          <Button href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" variant="secondary">
            <Instagram className="size-5" strokeWidth={1.75} aria-hidden />
            {t("ui.landing.faq.instagram")}
          </Button>
        </div>
      </section>
    );

  // Arrow keys follow the reading direction (left is "next" in RTL); selection follows focus, as radios do.
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const n = plans.length;
    const rtl = getComputedStyle(e.currentTarget).direction === "rtl";
    const step = ({ ArrowLeft: rtl ? 1 : -1, ArrowRight: rtl ? -1 : 1, ArrowDown: 1, ArrowUp: -1 } as Record<string, number>)[e.key];
    const j = step !== undefined ? (sel + step + n) % n : e.key === "Home" ? 0 : e.key === "End" ? n - 1 : -1;
    if (j < 0) return;
    e.preventDefault();
    setSel(j);
    radios.current[j]?.focus();
  };

  const cols = { gridTemplateColumns: `repeat(${plans.length}, minmax(0, 1fr))` };
  const priced = plan.total !== null && currency !== null;
  // Guests have no currency yet: the read-out shows the duration and the saving instead of a price.
  const big = priced ? plan.total! : plan.months;
  const saving = plan.saving > 0 ? t("ui.landing.plans.saving", { percent: formatPercent(plan.saving) }) : t("ui.landing.b.plans.monthly");

  return (
    <section id="plans" aria-labelledby={titleId} className={section}>
      <div className="grid gap-7 [grid-template-areas:'head'_'seg'_'card'_'perks'] min-[900px]:grid-cols-[7fr_5fr] min-[900px]:grid-rows-[auto_auto_1fr] min-[900px]:items-start min-[900px]:gap-x-[clamp(40px,6vw,96px)] min-[900px]:gap-y-8 min-[900px]:[grid-template-areas:'head_card'_'seg_card'_'perks_card']">
        <Reveal className="[grid-area:head]">{head}</Reveal>

        <Reveal delay={0.08} className="relative pt-4 [grid-area:seg]">
          <div role="radiogroup" aria-labelledby={titleId} onKeyDown={onKey} className="grid rounded-md border border-line bg-bg-2 p-[5px]" style={cols}>
            {plans.map((p, i) => {
              const on = i === sel;
              return (
                <button
                  key={p.plan}
                  ref={(el) => {
                    radios.current[i] = el;
                  }}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  aria-describedby={p.isBest ? bestId : undefined}
                  tabIndex={on ? 0 : -1}
                  onClick={() => setSel(i)}
                  className={cn(
                    "relative h-[50px] rounded-sm font-display text-[15.5px] font-bold transition-[color,transform] duration-200 ease-out active:scale-[.96]",
                    on ? "text-accent-text" : "text-ink-2 hover:text-ink",
                  )}
                >
                  {on && (
                    <motion.span
                      layoutId={`${uid}-thumb`}
                      aria-hidden
                      // the accent ring keeps the checked segment 3:1 from the track (the raised fill alone is ~1.1:1)
                      className="absolute inset-0 rounded-sm bg-raised shadow-[0_1px_2px_rgb(13_19_38/.08),0_6px_14px_-6px_rgb(13_19_38/.2)] ring-[1.5px] ring-inset ring-accent-text"
                      transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 520, damping: 42 }}
                    />
                  )}
                  <span className="relative">{t("ui.landing.plans.months", { count: formatNumber(p.months) })}</span>
                </button>
              );
            })}
          </div>
          {best >= 0 && (
            <div aria-hidden className="pointer-events-none absolute inset-x-[5px] top-[3px] grid" style={cols}>
              <span
                id={bestId}
                style={{ gridColumnStart: best + 1 }}
                className="justify-self-center whitespace-nowrap rounded-sm bg-accent px-2.5 font-display text-[11.5px] font-bold leading-[1.75] text-on-accent"
              >
                {t("ui.landing.plans.best")}
              </span>
            </div>
          )}
        </Reveal>

        {/* the art overhangs the card top: below 900px extra margin keeps it (and its float) off the duration control,
            from 900px it is smaller and the price sits lower so the art never reaches the number */}
        <Reveal
          delay={0.08}
          className={cn("relative rounded-lg border border-line bg-surface p-[clamp(24px,3vw,40px)] shadow-card [grid-area:card]", eq && "mt-5 md:mt-8 min-[900px]:mt-0")}
        >
          {eq && <EquivalentArt equivalent={eq} size={140} className="absolute -top-9 end-3 !size-[112px] md:-top-12 md:end-5 md:!size-[144px] min-[900px]:-top-14 min-[900px]:!size-[120px]" />}
          <p className={cn("font-display font-bold leading-[1.6] text-ink-2", eq && "pe-[104px] md:pe-[136px]")}>{plan.name}</p>
          <p aria-live="polite" aria-atomic className={cn("mb-0.5 mt-1 flex items-baseline gap-3 whitespace-nowrap", eq && "min-[900px]:mt-4")}>
            <Count value={big} fraction={priced && currency!.code !== "IRT" ? 2 : 0} className="font-display text-[clamp(3.25rem,2rem+3.6vw,4.75rem)] font-black tabular-nums leading-[1.15] text-ink" />
            <span className="font-display text-lg font-bold text-ink-2">{priced ? currency!.symbol : t("ui.landing.b.plans.months")}</span>
          </p>
          {eq && (
            // two lines reserved on phones: long items wrap there, and the card should not jump between plans
            <p className="mt-1 max-sm:min-h-16" aria-live="polite">
              <EquivalentLine equivalent={eq} className="text-lg [&>b]:text-2xl" />
            </p>
          )}
          <p className="mt-0.5 min-h-[1.9em] text-[15px] font-semibold text-accent-text">{saving}</p>
          {!priced && <p className="mt-1 text-sm leading-[1.8] text-ink-3">{t("ui.landing.plans.guestPrice")}</p>}
          <Button
            to={checkoutHref(plan.plan)}
            prefetch="intent"
            size="lg"
            block
            className={cn(
              "mt-6 px-[18px] shadow-[inset_0_1px_0_rgb(255_255_255/.18),0_10px_24px_-12px_rgb(53_107_206/.7)]",
              priced && "justify-between",
            )}
          >
            <span>{t("ui.landing.plans.buy", { name: plan.name })}</span>
            {priced && <span className="font-extrabold opacity-90">{formatMoney(plan.total!, currency!)}</span>}
          </Button>
          <p className="mt-4 flex gap-2 text-sm leading-[1.8] text-ink-3">
            <Info className="mt-[0.2em] size-[18px] shrink-0" strokeWidth={1.75} aria-hidden />
            {t("ui.landing.plans.paymentNote")}
          </p>
        </Reveal>

        <Reveal delay={0.16} className="[grid-area:perks]">
          <ul className="grid gap-x-7 gap-y-3 min-[560px]:grid-cols-2">
            {FEATURES.map((k) => (
              <li key={k} className="flex items-start gap-2.5 text-ink-2">
                <Check className="mt-[0.3em] size-5 shrink-0 text-accent-text" strokeWidth={1.75} aria-hidden />
                {t(`ui.landing.features.items.${k}.title`)}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}

// Tweens the shown number to the new value. The text is written straight to the node, so React never re-renders per frame;
// screen readers get the final value from the sibling sr-only text.
function Count({ value, fraction, className }: { value: number; fraction: number; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const shown = useRef(value);
  const [first] = useState(value);
  const reduce = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    const from = shown.current;
    const write = (v: number) => {
      shown.current = v;
      if (el) el.textContent = formatNumber(v, fraction);
    };
    if (reduce || from === value) return write(value);
    const c = animate(from, value, { duration: 0.65, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => write(v === value ? v : Math.round(v)) });
    return () => c.stop();
  }, [value, fraction, reduce]);

  return (
    <>
      <span ref={ref} aria-hidden className={className}>
        {formatNumber(first, fraction)}
      </span>
      <span className="sr-only">{formatNumber(value, fraction)}</span>
    </>
  );
}
